import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';

export const Section: FC<JSX.IntrinsicElements['section']> = ({
  className,
  ...props
}) => <section className={clsx('py-6 text-center', className)} {...props} />;
Section.displayName = 'Section';

export const Container: FC<JSX.IntrinsicElements['div']> = ({
  className,
  ...props
}) => {
  return <div {...props} className={clsx('container', className)} />;
};

export const ButtonSizes = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

type ButtonSize = (typeof ButtonSizes)[keyof typeof ButtonSizes];

export const BUTTON_TYPE_LINK = 'link';
export const BUTTON_TYPE_ROUTE = 'route';

export const BUTTONS_TYPES = [
  BUTTON_TYPE_LINK,
  BUTTON_TYPE_ROUTE,
  'button',
  'submit',
] as const;

type ButtonType = (typeof BUTTONS_TYPES)[number];

const BUTTON_STYLES = [
  'active',
  'disabled',
  'primary',
  'success',
  'error',
  'warning',
  'secondary',
] as const;
type ButtonStyle = (typeof BUTTON_STYLES)[number];

export type ButtonProps = {
  children?: ReactNode;
  className?: string;
  type: ButtonType;
  size?: ButtonSize;
} & Partial<Record<ButtonStyle, boolean>> &
  (
    | ({ type: 'route' } & LinkProps)
    | ({ type: 'link' } & JSX.IntrinsicElements['a'])
    | ({
        type: 'button' | 'submit';
      } & JSX.IntrinsicElements['button'])
  );

export const Button: FC<ButtonProps> = ({ className, size, ...props }) => {
  const classOpts: Record<string, boolean | undefined> = {
    'pure-button': true,
  };

  BUTTON_STYLES.forEach((attr) => {
    if (attr in props) {
      classOpts[`pure-button-${attr}`] = props[attr];
      delete props[attr];
    }
  });

  if (size) {
    classOpts[`pure-button-${ButtonSizes[size]}`] = true;
  }

  className = clsx(classOpts, className);

  switch (props.type) {
    case BUTTON_TYPE_ROUTE: {
      return <Link {...props} className={className} />;
    }
    case BUTTON_TYPE_LINK: {
      const href = props.href || '#';
      return <a {...props} className={className} href={href} />;
    }
    default: {
      const type = props.type || 'button';
      return <button {...props} className={className} type={type} />;
    }
  }
};

Object.assign(Button, {
  TYPE_LINK: BUTTON_TYPE_LINK,
  TYPE_ROUTE: BUTTON_TYPE_ROUTE,
});

export const PrimaryButton: FC<ButtonProps> = (props) => (
  <Button {...props} primary={true} />
);

// const FORM_STYLES = ['stacked', 'aligned'] as const;
// type FormStyle = (typeof FORM_STYLES)[number];

type FormStyle = 'stacked' | 'aligned';

type FormProps = JSX.IntrinsicElements['form'] &
  Partial<Record<FormStyle, boolean>>;

export const Form: FC<FormProps> = ({
  className,
  stacked,
  aligned,
  ...props
}) => {
  const classOpts = {
    'pure-form': true,
    'pure-form-stacked': !!stacked,
    'pure-form-aligned': !!aligned,
  };
  return <form className={clsx(className, classOpts)} {...props} />;
};

// just in case these need to be updated in a centralized place...
export const Input: FC<
  JSX.IntrinsicElements['input'] & { hasError?: boolean }
> = ({ className, hasError, ...props }) => {
  if (hasError) {
    className = clsx(className, 'has-error');
  }
  return <input type="text" className={className} {...props} />;
};

export const Label: FC<JSX.IntrinsicElements['label']> = (props) => (
  <label {...props} />
);

export const FormBody: FC<JSX.IntrinsicElements['div']> = ({
  className,
  ...props
}) => {
  return <div {...props} className={clsx(className, 'form-body')} />;
};

export const FormGroup: FC<JSX.IntrinsicElements['div']> = ({
  className,
  ...props
}) => {
  return <div {...props} className={clsx(className, 'form-group')} />;
};

export const FormButtons: FC<
  JSX.IntrinsicElements['div'] & {
    reverse?: boolean;
  }
> = ({ className, reverse = true, ...props }) => {
  return (
    <div
      {...props}
      className={clsx(className, 'form-buttons', 'split-items', {
        'split-items-reverse': reverse,
      })}
    />
  );
};

type CardSectionProps = JSX.IntrinsicElements['div'];

export const CardSection: FC<CardSectionProps> = ({ className, ...props }) => {
  return <div {...props} className={clsx(className, 'card-section')} />;
};

export const CardSectionMinimal: FC<CardSectionProps> = ({
  className,
  ...props
}) => (
  <CardSection {...props} className={clsx(className, 'card-section-minimal')} />
);
